import { Link as RouterLink } from 'react-router-dom'
import { Box, Link, Stack, TableBody, TableHead, TableRow } from '@mui/material'

import { Button, Card, Skeleton } from '@repo/ui'
import { useCanAccess, useReferrerState } from 'hooks'
import { useCompetitorContent } from 'hooks/api'
import { MARKET_RESEARCH_TITLE, ROUTES } from 'routes'

import { Cell, EmptyRow, HeadCell, SectionNote, StatTable, shortDate } from '../InternalAnalyticsPage/components/shared'
import { CompetitorLink } from './CompetitorLink'

/**
 * VRG-744 — the Content tab: competitors' own posts in the market-data corpus, newest first. Each
 * title opens the item in Market research; nothing is copied out of the corpus. Needs
 * `internal_market_intel_view` as well as this page's gate, because it reads the corpus.
 */
export const CompetitorContentTab = () => {
  const { isHasAccess: canReadCorpus } = useCanAccess({ action: 'internal_market_intel_view' })
  const { data, isLoading, error } = useCompetitorContent(canReadCorpus)
  const referrer = useReferrerState()

  if (!canReadCorpus)
    return (
      <Card>
        <SectionNote>
          Competitors&rsquo; posts come from {MARKET_RESEARCH_TITLE}, which needs the market-data view permission.
        </SectionNote>
      </Card>
    )
  if (isLoading)
    return (
      <Card>
        <Skeleton height={14} lines={8} />
      </Card>
    )
  if (error || !data)
    return (
      <Card>
        <SectionNote>{(error as Error | null)?.message ?? 'Could not load competitor content.'}</SectionNote>
      </Card>
    )

  return (
    <Card
      title="Competitor content"
      action={
        <Button variant="secondary" component={RouterLink} to={ROUTES.INTERNAL_MARKET}>
          Open {MARKET_RESEARCH_TITLE}
        </Button>
      }
    >
      <Stack spacing={4} sx={{ mt: 4 }}>
        <SectionNote>
          {data.basis === 'feed'
            ? 'Items from the market sources linked to a competitor, newest first. Rejected items are left out.'
            : 'Items whose link is on a competitor’s own site, newest first. Rejected items are left out. Once market sources carry their competitor (VRG-741), this lists every item from a competitor’s feeds instead.'}
        </SectionNote>
        <StatTable>
          <TableHead>
            <TableRow>
              <HeadCell>Published</HeadCell>
              <HeadCell>Title</HeadCell>
              <HeadCell>Competitor</HeadCell>
              <HeadCell>Source</HeadCell>
              <HeadCell>Status</HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.length === 0 ? (
              <EmptyRow colSpan={5} text="No competitor posts in the corpus yet. Add a competitor's feed as a market source." />
            ) : (
              data.items.map((i) => (
                <TableRow key={i.id}>
                  <Cell>{shortDate(i.publishedAt ?? i.retrievedAt)}</Cell>
                  <Cell>
                    <Box sx={{ whiteSpace: 'normal', minWidth: 280, maxWidth: 560, lineHeight: 1.5 }}>
                      <Link component={RouterLink} to={ROUTES.INTERNAL_MARKET_CONTENT(i.id)} state={referrer} underline="hover" color="inherit">
                        {i.title?.trim() || i.url}
                      </Link>
                    </Box>
                  </Cell>
                  <Cell strong>
                    <CompetitorLink domain={i.competitorDomain} name={i.competitorName} strong />
                  </Cell>
                  <Cell>{i.sourceName ?? i.publication ?? '—'}</Cell>
                  <Cell>{i.status.charAt(0).toUpperCase() + i.status.slice(1)}</Cell>
                </TableRow>
              ))
            )}
          </TableBody>
        </StatTable>
      </Stack>
    </Card>
  )
}
